import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { StatusBadge } from "../components/Badges";
import { toast } from "sonner";
import { Search, AlertTriangle, Repeat, CheckCircle2 } from "lucide-react";

export default function NewComplaint() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ product_types: [], priorities: [] });
  const [searchMobile, setSearchMobile] = useState("");
  const [searchBill, setSearchBill] = useState("");
  const [detection, setDetection] = useState(null);

  const [customer, setCustomer] = useState({ name: "", mobile: "", alternate_phone: "", email: "", address: "", city: "", pincode: "" });
  const [existingCustomerId, setExistingCustomerId] = useState(null);
  const [appliances, setAppliances] = useState([]);
  const [applianceId, setApplianceId] = useState("");
  const [newAppliance, setNewAppliance] = useState({ product_type: "Air Conditioner", brand: "", model_number: "", serial_number: "", purchase_date: "", warranty_status: "Out of Warranty", bill_number: "" });
  const [useNewAppliance, setUseNewAppliance] = useState(true);

  const [issue, setIssue] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const runSearch = async () => {
    if (!searchMobile && !searchBill) {
      toast.error("Enter a mobile number or bill number to search.");
      return;
    }
    const r = await api.get("/complaints/lookup", { params: { mobile: searchMobile, bill_number: searchBill } });
    setDetection(r.data);
    if (r.data.customer) {
      setCustomer(r.data.customer);
      setExistingCustomerId(r.data.customer.id);
      setAppliances(r.data.appliances || []);
      if (r.data.appliances?.length) {
        setUseNewAppliance(false);
        setApplianceId(r.data.appliances[0].id);
      }
      toast.success("Existing customer found and loaded.");
    } else {
      setExistingCustomerId(null);
      setAppliances([]);
      setUseNewAppliance(true);
      setCustomer((c) => ({ ...c, mobile: searchMobile || c.mobile }));
      setNewAppliance((a) => ({ ...a, bill_number: searchBill || a.bill_number }));
      toast.info("No existing customer — fill details to create a new one.");
    }
  };

  const submit = async () => {
    if (!customer.name || !customer.mobile) return toast.error("Customer name and mobile are required.");
    if (!issue) return toast.error("Issue category is required.");
    if (!useNewAppliance && !applianceId) return toast.error("Select an appliance.");
    if (useNewAppliance && !newAppliance.product_type) return toast.error("Select a product type.");

    setSubmitting(true);
    const payload = {
      issue_category: issue, description, priority,
      ...(existingCustomerId ? { customer_id: existingCustomerId } : { customer }),
      ...(useNewAppliance
        ? { appliance: { ...newAppliance, customer_id: existingCustomerId || "" } }
        : { appliance_id: applianceId }),
    };
    try {
      const r = await api.post("/complaints", payload);
      toast.success(`Complaint ${r.data.complaint_no} registered.`);
      navigate(`/complaints/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-5 sm:p-8 max-w-4xl fade-up">
      <div className="mb-6">
        <div className="label-caps text-muted-foreground">Intake</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">New Complaint</h1>
      </div>

      {/* Step 1: Search */}
      <section className="border border-border bg-white p-5 mb-5">
        <div className="label-caps text-klein mb-3">Step 1 — Search customer history</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input placeholder="Customer mobile" value={searchMobile} data-testid="search-mobile"
            onChange={(e) => setSearchMobile(e.target.value)} className="rounded-sm" />
          <Input placeholder="Bill / invoice number" value={searchBill} data-testid="search-bill"
            onChange={(e) => setSearchBill(e.target.value)} className="rounded-sm" />
          <Button onClick={runSearch} data-testid="search-history-button" className="rounded-sm bg-ink hover:bg-ink/90 text-white">
            <Search size={15} className="mr-1" /> Search
          </Button>
        </div>

        {detection && (detection.is_recurring || detection.is_repeat) && (
          <div data-testid="repeat-alert" className={`mt-4 border p-4 ${detection.is_recurring ? "border-signal bg-signal/10" : "border-warn bg-warn/15"}`}>
            <div className="flex items-center gap-2 font-bold">
              {detection.is_recurring ? <AlertTriangle size={18} className="text-signal" /> : <Repeat size={18} />}
              {detection.is_recurring ? "Recurring complaint — repair may not have held!" : "Repeat customer"}
            </div>
            <div className="mt-2 space-y-1">
              {detection.prior.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs text-klein">{p.complaint_no}</span>
                  <StatusBadge status={p.status} />
                  <span className="text-muted-foreground">{p.issue_category}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {detection && !detection.is_repeat && (
          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={16} /> No prior complaints found.</div>
        )}
      </section>

      {/* Step 2: Customer */}
      <section className="border border-border bg-white p-5 mb-5">
        <div className="label-caps text-klein mb-3">Step 2 — Customer {existingCustomerId && "(existing)"}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="label-caps">Name *</Label><Input value={customer.name} data-testid="cust-name" onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div><Label className="label-caps">Mobile *</Label><Input value={customer.mobile} data-testid="cust-mobile" onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div><Label className="label-caps">Alternate phone</Label><Input value={customer.alternate_phone} onChange={(e) => setCustomer({ ...customer, alternate_phone: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div><Label className="label-caps">Email</Label><Input value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div className="sm:col-span-2"><Label className="label-caps">Address</Label><Input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div><Label className="label-caps">City</Label><Input value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div><Label className="label-caps">Pincode</Label><Input value={customer.pincode} onChange={(e) => setCustomer({ ...customer, pincode: e.target.value })} className="mt-1 rounded-sm" /></div>
        </div>
      </section>

      {/* Step 3: Appliance */}
      <section className="border border-border bg-white p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-caps text-klein">Step 3 — Appliance</div>
          {appliances.length > 0 && (
            <div className="flex gap-2 text-xs">
              <button data-testid="toggle-existing-appliance" onClick={() => setUseNewAppliance(false)} className={`px-2 py-1 border ${!useNewAppliance ? "bg-klein text-white border-klein" : "border-border"}`}>Existing</button>
              <button data-testid="toggle-new-appliance" onClick={() => setUseNewAppliance(true)} className={`px-2 py-1 border ${useNewAppliance ? "bg-klein text-white border-klein" : "border-border"}`}>New</button>
            </div>
          )}
        </div>

        {!useNewAppliance && appliances.length > 0 ? (
          <Select value={applianceId} onValueChange={setApplianceId}>
            <SelectTrigger data-testid="select-appliance" className="rounded-sm"><SelectValue placeholder="Select appliance" /></SelectTrigger>
            <SelectContent>
              {appliances.map((a) => <SelectItem key={a.id} value={a.id}>{a.product_type} · {a.brand} {a.model_number} ({a.warranty_status})</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-caps">Product type *</Label>
              <Select value={newAppliance.product_type} onValueChange={(v) => setNewAppliance({ ...newAppliance, product_type: v })}>
                <SelectTrigger data-testid="appliance-type" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.product_types.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-caps">Brand</Label><Input value={newAppliance.brand} onChange={(e) => setNewAppliance({ ...newAppliance, brand: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label className="label-caps">Model number</Label><Input value={newAppliance.model_number} onChange={(e) => setNewAppliance({ ...newAppliance, model_number: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label className="label-caps">Serial number</Label><Input value={newAppliance.serial_number} onChange={(e) => setNewAppliance({ ...newAppliance, serial_number: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label className="label-caps">Bill / invoice number</Label><Input value={newAppliance.bill_number} data-testid="appliance-bill" onChange={(e) => setNewAppliance({ ...newAppliance, bill_number: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div>
              <Label className="label-caps">Warranty</Label>
              <Select value={newAppliance.warranty_status} onValueChange={(v) => setNewAppliance({ ...newAppliance, warranty_status: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="In Warranty">In Warranty</SelectItem>
                  <SelectItem value="Out of Warranty">Out of Warranty</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </section>

      {/* Step 4: Issue */}
      <section className="border border-border bg-white p-5 mb-5">
        <div className="label-caps text-klein mb-3">Step 4 — Issue</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="label-caps">Issue category *</Label><Input value={issue} data-testid="issue-category" onChange={(e) => setIssue(e.target.value)} placeholder="e.g. Not cooling" className="mt-1 rounded-sm" /></div>
          <div>
            <Label className="label-caps">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="priority-select" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label className="label-caps">Description</Label><Textarea value={description} data-testid="issue-description" onChange={(e) => setDescription(e.target.value)} className="mt-1 rounded-sm" rows={3} /></div>
        </div>
      </section>

      <Button onClick={submit} disabled={submitting} data-testid="submit-complaint-button"
        className="rounded-sm h-11 px-8 bg-klein hover:bg-klein/90 text-white font-semibold">
        {submitting ? "Registering…" : "Register Complaint"}
      </Button>
    </div>
  );
}
